/**
 * PetServiceBookingFlow - Booking wizard for pet services
 * Steps: Pet → Service → Appointment → Details → Payment
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Dog, Cat, Bird, Fish, Stethoscope, Scissors, GraduationCap,
  ChevronRight, ChevronLeft, CheckCircle, MapPin, Clock,
  Calendar as CalendarIcon, CreditCard, User, Mail, Phone,
  Plus, Loader2, Star, Shield, Home, Video
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Steps configuration
const STEPS = [
  { id: 1, name: 'Pet', icon: Dog, description: 'Select your pet' },
  { id: 2, name: 'Service', icon: Stethoscope, description: 'Choose service' },
  { id: 3, name: 'Appointment', icon: CalendarIcon, description: 'Pick date & time' },
  { id: 4, name: 'Details', icon: User, description: 'Your information' },
  { id: 5, name: 'Confirm', icon: CreditCard, description: 'Review & pay' },
];

// Pet type icons
const PET_ICONS = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  fish: Fish,
  rabbit: Dog,
  hamster: Dog,
  other: Dog,
};

// Service mode icons
const MODE_ICONS = {
  in_clinic: Stethoscope,
  home_visit: Home,
  video: Video,
  salon: Scissors,
  mobile: Home,
  outdoor: Dog,
  facility: Home,
  in_person: GraduationCap,
  online: Video,
};

// Step Progress Component
function StepProgress({ currentStep, steps }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id;
          
          return (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${isActive ? 'text-indigo-600' : isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-indigo-100 border-2 border-indigo-600' :
                  isComplete ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs mt-1 font-medium hidden sm:block">{step.name}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Step 1: Pet Selection
function PetStep({ pets, selectedPet, onSelectPet, onAddPet, onNext }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPet, setNewPet] = useState({
    type: 'dog',
    name: '',
    breed: '',
    size: 'medium',
    notes: '',
  });
  
  const handleAddPet = () => {
    if (!newPet.name) {
      toast.error('Please enter a pet name');
      return;
    }
    onAddPet(newPet);
    setShowAddForm(false);
    setNewPet({ type: 'dog', name: '', breed: '', size: 'medium', notes: '' });
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Dog className="w-5 h-5 text-indigo-600" />
          Select Your Pet
        </CardTitle>
        <CardDescription>
          Choose which pet needs the service
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing pets */}
        {pets.length > 0 && (
          <RadioGroup value={String(selectedPet)} onValueChange={(v) => onSelectPet(parseInt(v))}>
            <div className="grid gap-3">
              {pets.map((pet, index) => {
                const Icon = PET_ICONS[pet.type] || Dog;
                return (
                  <div 
                    key={index}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedPet === index ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => onSelectPet(index)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{pet.name}</p>
                        <p className="text-sm text-gray-500 capitalize">{pet.type} {pet.breed && `• ${pet.breed}`}</p>
                      </div>
                      <RadioGroupItem value={String(index)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </RadioGroup>
        )}
        
        {/* Add new pet */}
        {!showAddForm ? (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add a Pet
          </Button>
        ) : (
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="font-medium">Add New Pet</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pet Type</Label>
                <Select value={newPet.type} onValueChange={(v) => setNewPet({...newPet, type: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">🐕 Dog</SelectItem>
                    <SelectItem value="cat">🐈 Cat</SelectItem>
                    <SelectItem value="bird">🐦 Bird</SelectItem>
                    <SelectItem value="fish">🐟 Fish</SelectItem>
                    <SelectItem value="rabbit">🐰 Rabbit</SelectItem>
                    <SelectItem value="other">🐾 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Size</Label>
                <Select value={newPet.size} onValueChange={(v) => setNewPet({...newPet, size: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (&lt;10kg)</SelectItem>
                    <SelectItem value="medium">Medium (10-25kg)</SelectItem>
                    <SelectItem value="large">Large (25-40kg)</SelectItem>
                    <SelectItem value="xl">XL (&gt;40kg)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Pet Name *</Label>
              <Input 
                value={newPet.name}
                onChange={(e) => setNewPet({...newPet, name: e.target.value})}
                placeholder="e.g., Max, Bella"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Breed (optional)</Label>
              <Input 
                value={newPet.breed}
                onChange={(e) => setNewPet({...newPet, breed: e.target.value})}
                placeholder="e.g., Golden Retriever"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                value={newPet.notes}
                onChange={(e) => setNewPet({...newPet, notes: e.target.value})}
                placeholder="Any special needs or information..."
                className="mt-1"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button onClick={handleAddPet}>Add Pet</Button>
            </div>
          </div>
        )}
        
        <Button 
          className="w-full" 
          onClick={onNext}
          disabled={selectedPet === null && pets.length === 0}
        >
          Continue to Service
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

// Step 2: Service Selection
function ServiceStep({ provider, services, selectedService, onSelectService, onNext, onBack }) {
  const currencySymbol = provider?.country === 'LK' ? 'Rs.' : '£';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-indigo-600" />
          Choose Service
        </CardTitle>
        <CardDescription>
          Select a service from {provider?.display_name}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3">
          {services.map(service => (
            <div 
              key={service.service_id}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                selectedService === service.service_id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => onSelectService(service.service_id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{service.title}</p>
                  <p className="text-sm text-gray-500">{service.duration_mins} mins</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-indigo-600">
                    {currencySymbol}{service.base_price}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1" 
            onClick={onNext}
            disabled={!selectedService}
          >
            Continue to Appointment
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Step 3: Appointment Selection
function AppointmentStep({ provider, appointment, onAppointmentChange, onNext, onBack }) {
  const [date, setDate] = useState(appointment?.date ? new Date(appointment.date) : null);
  const [timeSlot, setTimeSlot] = useState(appointment?.time_slot || '');
  const [mode, setMode] = useState(appointment?.mode || provider?.service_modes?.[0] || 'in_clinic');
  const [address, setAddress] = useState(appointment?.address || '');
  
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30',
  ];
  
  const needsAddress = mode === 'home_visit' || mode === 'mobile';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-indigo-600" />
          Choose Appointment
        </CardTitle>
        <CardDescription>
          Select date, time, and appointment mode
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Service Mode */}
        <div>
          <Label className="mb-3 block">Appointment Type</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {provider?.service_modes?.map(m => {
              const Icon = MODE_ICONS[m] || Stethoscope;
              return (
                <Button 
                  key={m}
                  variant={mode === m ? 'default' : 'outline'}
                  className="justify-start"
                  onClick={() => setMode(m)}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {m.replace('_', ' ').replace(/^\w/, c => c.toUpperCase())}
                </Button>
              );
            })}
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <Label className="mb-2 block">Select Date</Label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < minDate}
              className="rounded-md border"
            />
          </div>
          
          {/* Time Slots */}
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Select Time</Label>
              <div className="grid grid-cols-3 gap-2">
                {timeSlots.map(slot => (
                  <Button 
                    key={slot}
                    variant={timeSlot === slot ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTimeSlot(slot)}
                  >
                    {slot}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Address for home visit */}
            {needsAddress && (
              <div>
                <Label className="mb-2 block">Your Address</Label>
                <Textarea 
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Enter your full address for the home visit..."
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>
        
        {date && timeSlot && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">
                {date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {timeSlot}
              </span>
            </div>
            <p className="text-sm text-green-700 mt-1 capitalize">
              {mode.replace('_', ' ')} appointment
            </p>
          </div>
        )}
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1" 
            onClick={() => onNext({ 
              date: date?.toISOString().split('T')[0], 
              time_slot: timeSlot, 
              mode,
              address: needsAddress ? address : null,
            })}
            disabled={!date || !timeSlot || (needsAddress && !address)}
          >
            Continue to Details
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Step 4: Customer Details
function DetailsStep({ customer, onCustomerChange, onNext, onBack }) {
  const [name, setName] = useState(customer?.name || '');
  const [email, setEmail] = useState(customer?.email || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [notes, setNotes] = useState(customer?.notes || '');
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="w-5 h-5 text-indigo-600" />
          Your Details
        </CardTitle>
        <CardDescription>
          Enter your contact information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Full Name *</Label>
          <div className="relative mt-1">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="pl-10"
            />
          </div>
        </div>
        
        <div>
          <Label>Email *</Label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="pl-10"
            />
          </div>
        </div>
        
        <div>
          <Label>Phone</Label>
          <div className="relative mt-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+44 7XXX XXXXXX"
              className="pl-10"
            />
          </div>
        </div>
        
        <div>
          <Label>Reason for Visit / Notes</Label>
          <Textarea 
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe the reason for your appointment..."
            className="mt-1"
            rows={3}
          />
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1" 
            onClick={() => onNext({ name, email, phone, notes })}
            disabled={!name || !email}
          >
            Review Booking
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Step 5: Confirmation
function ConfirmStep({ 
  provider, 
  service, 
  pet, 
  appointment, 
  customer, 
  pricing,
  onConfirm, 
  onBack,
  loading 
}) {
  const [agreeTerms, setAgreeTerms] = useState(false);
  const currencySymbol = provider?.country === 'LK' ? 'Rs.' : '£';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-indigo-600" />
          Review & Confirm
        </CardTitle>
        <CardDescription>
          Please review your booking details
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Provider */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">Provider</p>
          <p className="font-semibold">{provider?.display_name}</p>
          <p className="text-sm text-gray-600">{provider?.location?.city}</p>
        </div>
        
        {/* Service & Pet */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Service</p>
            <p className="font-medium">{service?.title}</p>
            <p className="text-sm text-gray-500">{service?.duration_mins} mins</p>
          </div>
          <div className="p-4 border rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Pet</p>
            <p className="font-medium">{pet?.name}</p>
            <p className="text-sm text-gray-500 capitalize">{pet?.type} {pet?.size && `• ${pet.size}`}</p>
          </div>
        </div>
        
        {/* Appointment */}
        <div className="p-4 border rounded-lg">
          <p className="text-sm text-gray-500 mb-1">Appointment</p>
          <p className="font-medium">
            {new Date(appointment?.date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-sm text-gray-600">
            {appointment?.time_slot} • {appointment?.mode?.replace('_', ' ')}
          </p>
          {appointment?.address && (
            <p className="text-sm text-gray-500 mt-1">{appointment.address}</p>
          )}
        </div>
        
        {/* Pricing */}
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Service</span>
            <span>{currencySymbol}{pricing?.base_price}</span>
          </div>
          {pricing?.platform_fee > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Platform fee</span>
              <span>{currencySymbol}{pricing?.platform_fee}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg pt-2 border-t">
            <span>Total</span>
            <span className="text-indigo-600">{currencySymbol}{pricing?.total}</span>
          </div>
        </div>
        
        {/* Terms */}
        <div className="flex items-start gap-2">
          <Checkbox 
            id="terms" 
            checked={agreeTerms}
            onCheckedChange={setAgreeTerms}
          />
          <label htmlFor="terms" className="text-sm text-gray-600">
            I agree to the Terms of Service and understand that the provider may contact me regarding this booking.
          </label>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600" 
            onClick={onConfirm}
            disabled={!agreeTerms || loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            Confirm Booking
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Success Step
function SuccessStep({ bookingId, provider, appointment }) {
  const navigate = useNavigate();
  
  return (
    <Card className="max-w-lg mx-auto">
      <CardContent className="py-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
        <p className="text-gray-500 mb-6">
          Your appointment has been successfully booked.
        </p>
        
        <div className="p-4 bg-gray-50 rounded-lg text-left mb-6">
          <p className="text-sm text-gray-500">Booking Reference</p>
          <p className="text-lg font-mono font-bold text-indigo-600">{bookingId?.slice(0, 8).toUpperCase()}</p>
          
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Provider</span>
              <span>{provider?.display_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span>{appointment?.date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time</span>
              <span>{appointment?.time_slot}</span>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-500 mb-6">
          A confirmation email has been sent with all the details.
        </p>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate('/pets')}
          >
            Back to Pets
          </Button>
          <Button 
            className="flex-1"
            onClick={() => navigate('/my-bookings')}
          >
            View Bookings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function PetServiceBookingFlow() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  
  // Booking data
  const [pets, setPets] = useState([]);
  const [selectedPetIndex, setSelectedPetIndex] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [customer, setCustomer] = useState(null);
  
  // Fetch provider and services
  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/pets/providers/${providerId}`);
        const data = await response.json();
        
        if (data.success) {
          setProvider(data.provider);
          setServices(data.services || []);
        }
      } catch (error) {
        console.error('Failed to fetch provider:', error);
        toast.error('Failed to load provider');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProvider();
  }, [providerId]);
  
  // Get selected service object
  const selectedService = useMemo(() => 
    services.find(s => s.service_id === selectedServiceId), 
    [services, selectedServiceId]
  );
  
  // Get selected pet object
  const selectedPet = useMemo(() => 
    selectedPetIndex !== null ? pets[selectedPetIndex] : null,
    [pets, selectedPetIndex]
  );
  
  // Calculate pricing
  const pricing = useMemo(() => {
    if (!selectedService || !provider) return null;
    
    let basePrice = selectedService.base_price;
    
    // Apply size multiplier for grooming
    if (provider.provider_type === 'GROOMER' && selectedPet?.size) {
      const sizeMultipliers = { small: 1.0, medium: 1.3, large: 1.6, xl: 2.0 };
      basePrice = Math.round(basePrice * (sizeMultipliers[selectedPet.size] || 1));
    }
    
    const platformFee = provider.country === 'LK' ? 150 : 2.50;
    
    return {
      base_price: basePrice,
      platform_fee: platformFee,
      total: basePrice + platformFee,
    };
  }, [selectedService, provider, selectedPet]);
  
  // Add pet
  const handleAddPet = (pet) => {
    setPets([...pets, pet]);
    setSelectedPetIndex(pets.length);
  };
  
  // Submit booking
  const handleConfirm = async () => {
    if (!selectedPet || !selectedService || !appointment || !customer) {
      toast.error('Please complete all booking details');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pets/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: providerId,
          service_id: selectedServiceId,
          pet: selectedPet,
          appointment: {
            mode: appointment.mode,
            date: appointment.date,
            time_slot: appointment.time_slot,
            duration_mins: selectedService.duration_mins,
            address: appointment.address,
          },
          customer_name: customer.name,
          customer_email: customer.email,
          customer_phone: customer.phone,
          notes: customer.notes,
          country: provider.country,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBookingId(data.booking_id);
        setStep(6);
        toast.success('Booking confirmed!');
      } else {
        toast.error(data.detail || 'Booking failed');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  if (!provider && !providerId) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">No Provider Selected</h1>
          <p className="text-gray-500 mb-6">Please select a provider from the Pets & Animals section.</p>
          <Button onClick={() => navigate('/pets')}>
            Browse Providers
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        {step < 6 && (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Book an Appointment</h1>
              <p className="text-gray-500">{provider?.display_name}</p>
            </div>
            
            <StepProgress currentStep={step} steps={STEPS} />
          </>
        )}
        
        {/* Steps */}
        {step === 1 && (
          <PetStep 
            pets={pets}
            selectedPet={selectedPetIndex}
            onSelectPet={setSelectedPetIndex}
            onAddPet={handleAddPet}
            onNext={() => setStep(2)}
          />
        )}
        
        {step === 2 && (
          <ServiceStep 
            provider={provider}
            services={services}
            selectedService={selectedServiceId}
            onSelectService={setSelectedServiceId}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}
        
        {step === 3 && (
          <AppointmentStep 
            provider={provider}
            appointment={appointment}
            onAppointmentChange={setAppointment}
            onNext={(appt) => {
              setAppointment(appt);
              setStep(4);
            }}
            onBack={() => setStep(2)}
          />
        )}
        
        {step === 4 && (
          <DetailsStep 
            customer={customer}
            onCustomerChange={setCustomer}
            onNext={(cust) => {
              setCustomer(cust);
              setStep(5);
            }}
            onBack={() => setStep(3)}
          />
        )}
        
        {step === 5 && (
          <ConfirmStep 
            provider={provider}
            service={selectedService}
            pet={selectedPet}
            appointment={appointment}
            customer={customer}
            pricing={pricing}
            onConfirm={handleConfirm}
            onBack={() => setStep(4)}
            loading={submitting}
          />
        )}
        
        {step === 6 && (
          <SuccessStep 
            bookingId={bookingId}
            provider={provider}
            appointment={appointment}
          />
        )}
      </div>
    </div>
  );
}
